<script>
	import { onMount, onDestroy } from "svelte";
	import CustomTable from "src/components/CustomTable/CustomTable.svelte";
	import CustomSelect from "src/components/CustomSelect/CustomSelect.svelte";
	import Loader from "src/components/Loader/Loader.svelte";
	import LimitSize from "src/components/SizeSelect/limitSize.svelte";
	import {
		initPage,
		totalRecords,
		totalPages,
		sizeSelect,
	} from "src/store/store";
	import { activityTypeOptions } from "src/utils/options";
	import { formatErrorMessage } from "src/utils/misc";
	import { getVehicleActivityLog } from "src/services/maintenanceRepair";
	import { activityLogSegments } from "./activityLogMessage";

	export let vehicleId;
	// Reports a load error to the vehicle page's banner ("" clears it).
	export let onError = (_message) => {};

	// The endpoint caps `size` at 20.
	const pageSizeOptions = [
		{ value: "10", label: "Show 10" },
		{ value: "20", label: "Show 20" },
	];

	const SORT_COLUMN = "occurredAt";
	const SORT_DIRECTIONS = ["ascending", "descending"];

	// CustomTable seeds its sort icon from ?sortCol/?sortDir when it mounts, and
	// all the M&R tabs share one route, so the URL may hold another tab's sort.
	// Settle it here, during init, because CustomTable's onMount runs before
	// this component's: keep the direction if it is ours (e.g. after a reload),
	// otherwise clear it so the icon and the fetched data both start at the
	// default.
	function resolveInitialSortDir() {
		const url = new URL(window.location.href);
		const sortCol = url.searchParams.get("sortCol");
		const sortDirParam = url.searchParams.get("sortDir");

		if (sortCol === SORT_COLUMN && SORT_DIRECTIONS.includes(sortDirParam)) {
			return sortDirParam;
		}
		if (sortCol !== null || sortDirParam !== null) {
			url.searchParams.delete("sortCol");
			url.searchParams.delete("sortDir");
			window.history.replaceState(window.history.state, "", url);
		}
		return "descending";
	}

	let isMounted = false;
	let activityType = "";
	let sortDir = resolveInitialSortDir();
	let rows = [];
	let totalCount = 0;
	let isLoading = true;
	let loadError = false;
	let latestRequestId = 0;

	function buildQueryParams(type, dir, page, size) {
		return {
			// "All activities" is the absence of the param, not an empty value.
			...(type ? { activityType: type } : {}),
			sortDir: dir === "ascending" ? "asc" : "desc",
			page,
			size,
		};
	}

	async function fetchActivityLog(id, params) {
		const requestId = ++latestRequestId;
		isLoading = true;
		loadError = false;
		try {
			const res = await getVehicleActivityLog(id, params);
			// Ignore responses superseded by a newer request.
			if (requestId !== latestRequestId) return;
			rows = res.data.items.map((entry) => ({
				...entry,
				messageSegments: activityLogSegments(entry),
			}));
			totalCount = res.data.totalCount;
			// Keep the page in range if the log shrank.
			const lastPage = Math.max(1, Math.ceil(totalCount / params.size));
			if (params.page > lastPage) initPage.set(lastPage);
			onError("");
		} catch (error) {
			if (requestId !== latestRequestId) return;
			console.error(error);
			rows = [];
			totalCount = 0;
			loadError = true;
			onError(
				formatErrorMessage(error) || "Couldn't load the activity log."
			);
		} finally {
			if (requestId === latestRequestId) isLoading = false;
		}
	}

	// The vehicle can change without a remount (back/forward).
	let currentVehicleId = vehicleId;
	$: if (vehicleId !== currentVehicleId) {
		currentVehicleId = vehicleId;
		rows = [];
		totalCount = 0;
		initPage.set(1);
	}

	// Waits for mount so the first request uses this tab's page size.
	$: queryParams = buildQueryParams(
		activityType,
		sortDir,
		$initPage,
		Number($sizeSelect)
	);
	$: if (isMounted) fetchActivityLog(vehicleId, queryParams);

	$: totalRecords.set(totalCount);
	$: totalPages.set(Math.max(1, Math.ceil(totalCount / Number($sizeSelect))));

	// Changing the filter or sort invalidates the current page number (US-6
	// specifies the type filter refetches from page 1).
	function resetToFirstPage() {
		initPage.set(1);
	}

	function handleSort({ detail: { direction } }) {
		sortDir = direction;
		resetToFirstPage();
	}

	onMount(() => {
		initPage.set(1);
		sizeSelect.set("10");
		isMounted = true;
	});

	// Drop this tab's sort from the shared URL on the way out so the next tab's
	// CustomTable doesn't inherit it. Only when it is ours: on a full navigation
	// the URL may already belong to the next page by the time this runs.
	function clearOwnSortFromUrl() {
		const url = new URL(window.location.href);
		if (url.searchParams.get("sortCol") !== SORT_COLUMN) return;
		url.searchParams.delete("sortCol");
		url.searchParams.delete("sortDir");
		window.history.replaceState(window.history.state, "", url);
	}

	onDestroy(() => {
		latestRequestId++;
		initPage.set(1);
		sizeSelect.set("20");
		totalPages.set(1);
		totalRecords.set(0);
		clearOwnSortFromUrl();
	});
</script>

<div
	class="mb-4 mt-4 d-flex justify-content-between align-items-center activity-log-filters"
>
	<CustomSelect
		id="activityType"
		name="activityType"
		label="Filter by activity type"
		defaultOption="All activities"
		bind:value={activityType}
		options={activityTypeOptions}
		onChange={resetToFirstPage}
	/>

	<div class="activity-log-page-size">
		<LimitSize options={pageSizeOptions} />
	</div>
</div>

{#if isLoading}
	<Loader />
{:else}
	<CustomTable
		columns={[
			{
				key: "occurredAt",
				label: "Date & time",
				type: "date",
				sortCol: "occurredAt",
				format: "DD MMM YYYY, LT",
				className: "text-nowrap",
			},
			{ key: "actionBy", label: "Action by" },
			{
				key: "messageSegments",
				label: "Activity log",
				type: "richText",
			},
		]}
		{rows}
		emptyMessage={loadError
			? "Couldn't load the activity log."
			: "No activity yet"}
		useNavigation
		on:sort={handleSort}
	/>
{/if}

<style>
	.activity-log-filters :global(select#activityType) {
		width: 250px;
	}
</style>
